import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { currentMonthKey } from './arBillingDates';
import { PROCEDURE_KIND_LABELS, REGION_LABELS } from '@claimup/shared/helpers/judicial-pricing';
import { EXPEDIENT_TYPE_LABELS } from '@claimup/shared/constants/expedient';

/**
 * La pestaña «Recobro» de Facturación (docs/facturacion-cliente-plan.md): lo
 * cobrado en el mes en los expedientes de recobro de la empresa, expediente a
 * expediente, y los honorarios que salen de sus precios. Como «Importes
 * recuperados», pero solo lo suyo.
 */

const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

const fecha = iso =>
  new Date(iso).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric' });

const FASE = { 'extra-judicial': EXPEDIENT_TYPE_LABELS['extra-judicial'], judicial: EXPEDIENT_TYPE_LABELS.judicial };

function RecoveryBilling({ companyId }) {
  const mesActual = currentMonthKey();
  const [month, setMonth] = useState(mesActual);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api
      .get(`/recovery-billing/companies/${companyId}/months/${month}`)
      .then(({ data: res }) => setData(res.data))
      .catch(err => {
        setData(null);
        toast.error(getErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, month]);

  const b = data?.billing;

  return (
    <div className='space-y-4'>
      <div className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-3'>
        <p className='text-sm text-slate-500 max-w-2xl'>
          Lo cobrado en el mes en vuestros expedientes de recobro, cobro a cobro, y los honorarios que corresponden. Las
          quitas y cancelaciones no son cobros y no llevan honorarios.
        </p>
        <label className='text-sm text-slate-600 flex items-center gap-2'>
          Mes
          <input
            type='month'
            aria-label='Mes de recobro'
            value={month}
            max={mesActual}
            onChange={e => e.target.value && setMonth(e.target.value)}
            className='form-input rounded-lg border-slate-300 text-sm'
          />
        </label>
      </div>

      {loading && <p className='text-sm text-slate-500'>Cargando…</p>}

      {!loading && data && (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            <Stat label='Cobrado en extrajudicial' value={eur(data.collected.extrajudicial)} />
            <Stat label='Cobrado en judicial' value={eur(data.collected.judicial)} />
            <Stat label='Total cobrado' value={eur(data.collected.total)} strong />
          </div>

          {!b ? (
            <div className='bg-white border border-slate-200 rounded-lg px-5 py-4 text-sm text-slate-500'>
              Honorarios sin definir: ClaimUp todavía no ha fijado vuestros precios de recobro.
            </div>
          ) : (
            <div className='bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-end justify-between gap-4'>
              <div>
                <div className='text-xs text-slate-500'>Honorarios del mes · sin IVA</div>
                <div className='mt-1 text-2xl font-semibold text-slate-900 tabular-nums'>{eur(b.fee)}</div>
                <div className='text-xs text-slate-400 mt-0.5'>
                  + IVA (21 %) {eur(b.vat)} = {eur(b.fee_with_vat)}
                </div>
              </div>
              <div className='text-sm text-slate-600 text-right space-y-0.5'>
                <div>
                  Extrajudicial: {b.extrajudicial.pct} % de {eur(b.extrajudicial.collected)} = {eur(b.extrajudicial.fee)}
                </div>
                <div>
                  {b.judicial.billed
                    ? `Judicial: ${b.judicial.pct} % de ${eur(b.judicial.collected)} = ${eur(b.judicial.fee)}`
                    : 'Judicial: no se factura'}
                </div>
                {b.capped && (
                  <div className='text-amber-700'>
                    Con el tope de {eur(b.cap)} al mes (saldría {eur(b.subtotal)})
                  </div>
                )}
                {!data.judicial && (
                  <div className='text-xs text-slate-400'>Las tasas judiciales (suplidos) se añaden en la factura.</div>
                )}
              </div>
            </div>
          )}

          {data.judicial && <Judicial judicial={data.judicial} totals={data.totals} />}

          <section className='bg-white border border-slate-200 rounded-lg overflow-x-auto'>
            <h2 className='px-5 pt-4 font-semibold text-slate-800'>Cobros del mes ({data.items.length})</h2>
            {data.items.length === 0 ? (
              <p className='px-5 py-4 text-sm text-slate-500'>No hay cobros en este mes.</p>
            ) : (
              <table className='min-w-full text-sm mt-2'>
                <thead className='bg-slate-50 text-slate-500 text-xs uppercase'>
                  <tr>
                    <th className='text-left px-4 py-2 font-semibold'>Fecha</th>
                    <th className='text-left px-4 py-2 font-semibold'>Deudor</th>
                    <th className='text-left px-4 py-2 font-semibold'>Referencia</th>
                    <th className='text-left px-4 py-2 font-semibold'>Fase</th>
                    <th className='text-left px-4 py-2 font-semibold'>Concepto</th>
                    <th className='text-right px-4 py-2 font-semibold'>Importe</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {data.items.map((i, k) => (
                    <tr key={`${i.expedient_id}-${k}`}>
                      <td className='px-4 py-2 tabular-nums'>{fecha(i.date)}</td>
                      <td className='px-4 py-2'>
                        <Link to={`/view/${i.expedient_id}`} className='text-indigo-600 hover:text-indigo-800'>
                          {i.deudor}
                        </Link>
                      </td>
                      <td className='px-4 py-2 text-slate-600'>{i.reference || '—'}</td>
                      <td className='px-4 py-2 text-slate-600'>{FASE[i.phase] || i.phase}</td>
                      <td className='px-4 py-2 text-slate-600'>{i.concept || '—'}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{eur(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className='bg-slate-50 font-semibold text-slate-800'>
                  <tr>
                    <td className='px-4 py-2' colSpan={5}>
                      Total cobrado
                    </td>
                    <td className='px-4 py-2 text-right tabular-nums'>{eur(data.collected.total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * La parte judicial del mes (docs/precios-judicial-plan.md): las demandas
 * presentadas, cada una con su precio y su tasa, y el total de la factura. Solo
 * sale si la empresa tiene precios judiciales.
 */
export function Judicial({ judicial, totals }) {
  return (
    <section className='bg-white border border-slate-200 rounded-lg overflow-x-auto'>
      <h2 className='px-5 pt-4 font-semibold text-slate-800'>Demandas presentadas en el mes ({judicial.items.length})</h2>
      {judicial.without_price > 0 && (
        <p className='px-5 pt-1 text-sm text-amber-700'>
          {judicial.without_price === 1 ? '1 demanda no suma' : `${judicial.without_price} demandas no suman`}: falta el
          tipo de procedimiento (monitorio o verbal) o su precio.
        </p>
      )}
      {judicial.items.length === 0 ? (
        <p className='px-5 py-4 text-sm text-slate-500'>No se ha presentado ninguna demanda este mes.</p>
      ) : (
        <table className='min-w-full text-sm mt-2'>
          <thead className='bg-slate-50 text-slate-500 text-xs uppercase'>
            <tr>
              <th className='text-left px-4 py-2 font-semibold'>Presentada</th>
              <th className='text-left px-4 py-2 font-semibold'>Deudor</th>
              <th className='text-left px-4 py-2 font-semibold'>Procedimiento</th>
              <th className='text-right px-4 py-2 font-semibold'>Reclamado</th>
              <th className='text-left px-4 py-2 font-semibold'>Región</th>
              <th className='text-right px-4 py-2 font-semibold'>Honorarios</th>
              <th className='text-right px-4 py-2 font-semibold'>Tasa (sin IVA)</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {judicial.items.map(i => (
              <tr key={i.expedient_id}>
                <td className='px-4 py-2 tabular-nums'>{fecha(i.filed_at)}</td>
                <td className='px-4 py-2'>
                  <Link to={`/view/${i.expedient_id}`} className='text-indigo-600 hover:text-indigo-800'>
                    {i.deudor}
                  </Link>
                </td>
                <td className='px-4 py-2 text-slate-600'>
                  {i.kind ? PROCEDURE_KIND_LABELS[i.kind] : <span className='text-amber-700'>Sin tipo</span>}
                </td>
                <td className='px-4 py-2 text-right tabular-nums'>{eur(i.claimed)}</td>
                <td className='px-4 py-2 text-slate-600'>{i.region ? REGION_LABELS[i.region] : '—'}</td>
                <td className='px-4 py-2 text-right tabular-nums'>{i.fee === null ? '—' : eur(i.fee)}</td>
                <td className='px-4 py-2 text-right tabular-nums'>{i.court_fee === null ? '—' : eur(i.court_fee)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {totals && (
        <div className='px-5 py-4 border-t border-slate-100 text-sm text-slate-700 flex flex-wrap justify-end gap-x-6 gap-y-1'>
          <span>Honorarios: {eur(totals.fees)}</span>
          <span>IVA (21 %): {eur(totals.vat)}</span>
          <span>Tasas (sin IVA): {eur(totals.court_fees)}</span>
          <span className='font-semibold text-slate-900'>Total: {eur(totals.total)}</span>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, strong }) {
  return (
    <div className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
      <div className='text-xs text-slate-500'>{label}</div>
      <div className={`mt-1 tabular-nums ${strong ? 'text-2xl font-semibold text-slate-900' : 'text-xl text-slate-800'}`}>
        {value}
      </div>
    </div>
  );
}

export default RecoveryBilling;
